import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { X, Globe, Building2, Users, Trash2, Plus } from "lucide-react";

const RULE_FIELDS = [
  { key: "min_rest", hoursLabel: "Descanso mínimo entre turnos (hs)", numKey: "min_rest_hours", modeKey: "min_rest_mode" },
  { key: "max_continuous", hoursLabel: "Máximo de horas corridas", numKey: "max_continuous_hours", modeKey: "max_continuous_mode" },
  { key: "max_consecutive", hoursLabel: "Máximo de días consecutivos sin franco", numKey: "max_consecutive_days", modeKey: "max_consecutive_mode" },
  { key: "max_weekly", hoursLabel: "Máximo de horas semanales", numKey: "max_weekly_hours", modeKey: "max_weekly_mode" },
  { key: "max_monthly", hoursLabel: "Máximo de horas mensuales por vigilador (0 = sin límite)", numKey: "max_monthly_hours", modeKey: "max_monthly_mode" }, // Fix AQ
  // Fix BP (2026-11-03): pedido del cliente. Medio franco = 0,5.
  { key: "max_days_off", hoursLabel: "Cantidad máxima de francos al mes (0 = sin límite · medio franco = 0,5)", numKey: "max_days_off_month", modeKey: "max_days_off_mode" },
];

// Fix BP: a qué turno aplica cada incompatibilidad. Noche = cruza la medianoche o empieza de 18:00 a 05:59.
const APPLIES_LABEL = { ambos: "Día y noche", dia: "Solo turno día", noche: "Solo turno noche" };

const overlayStyle = { position: "fixed", inset: 0, background: "rgba(10,10,10,0.7)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 210 };
const modalStyle = { background: "#fff", borderRadius: 12, padding: 20, width: "100%", maxWidth: 480, maxHeight: "88vh", overflowY: "auto" };

export function ScheduleRulesPanel({ onClose, canEdit, clientId, clientName, roster = [] }) {
  const [rules, setRules] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pairs, setPairs] = useState(null);
  const [newPair, setNewPair] = useState({ user_a_id: "", user_b_id: "", mode: "alert", applies_to: "ambos" });
  const [pairBusy, setPairBusy] = useState(false);

  const load = () => {
    api.get("/schedule/rules", { params: clientId ? { client_id: clientId } : {} })
      .then(({ data }) => setRules(data))
      .catch((e) => toast.error(formatApiError(e)));
  };

  const loadPairs = () => {
    api.get("/schedule/incompatibility-rules")
      .then(({ data }) => setPairs(data))
      .catch((e) => toast.error(formatApiError(e)));
  };

  useEffect(() => { setRules(null); load(); /* eslint-disable-next-line */ }, [clientId]);
  useEffect(() => { loadPairs(); }, []);

  const ownRules = !!(clientId && rules && rules.scope === "client");

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/schedule/rules", rules, { params: clientId ? { client_id: clientId } : {} });
      setRules(data);
      toast.success(clientId ? `Reglas de ${clientName || "el objetivo"} actualizadas` : "Reglas globales actualizadas");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const backToGlobal = async () => {
    if (!window.confirm(`${clientName || "Este objetivo"} volverá a usar las reglas globales. ¿Confirmás?`)) return;
    setBusy(true);
    try {
      const { data } = await api.delete("/schedule/rules", { params: { client_id: clientId } });
      setRules(data.rules);
      toast.success("El objetivo volvió a las reglas globales");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const addPair = async () => {
    if (!newPair.user_a_id || !newPair.user_b_id) return toast.error("Elegí las dos personas");
    if (newPair.user_a_id === newPair.user_b_id) return toast.error("Elegí dos personas distintas");
    setPairBusy(true);
    try {
      await api.post("/schedule/incompatibility-rules", newPair);
      toast.success("Regla de incompatibilidad creada");
      setNewPair({ user_a_id: "", user_b_id: "", mode: "alert", applies_to: "ambos" });
      loadPairs();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPairBusy(false); }
  };

  const updatePairMode = async (pid, mode) => {
    try { await api.patch(`/schedule/incompatibility-rules/${pid}`, { mode }); loadPairs(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const updatePairApplies = async (pid, applies_to) => {
    try { await api.patch(`/schedule/incompatibility-rules/${pid}`, { applies_to }); loadPairs(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const removePair = async (pid) => {
    if (!window.confirm("¿Eliminar esta regla de incompatibilidad?")) return;
    try { await api.delete(`/schedule/incompatibility-rules/${pid}`); toast.success("Regla eliminada"); loadPairs(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div style={overlayStyle} onClick={onClose} data-testid="rules-panel-overlay">
      <div style={modalStyle} onClick={(e) => e.stopPropagation()} data-testid="rules-panel">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 900, color: "#0A0A0A" }}>Reglas de cumplimiento</h3>
          <button onClick={onClose} style={{ border: "none", background: "transparent", cursor: "pointer" }} data-testid="rules-close"><X size={18} /></button>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 10px", borderRadius: 8, background: clientId ? "#fff5f5" : "var(--invict-bone-2)", border: `1px solid ${clientId ? "#f2c6c6" : "var(--invict-bone-3)"}`, margin: "8px 0 10px" }} data-testid="rules-scope-label">
          {clientId ? <Building2 size={14} style={{ color: "#BF3437", flexShrink: 0 }} /> : <Globe size={14} style={{ color: "#0A0A0A", flexShrink: 0 }} />}
          <b style={{ fontSize: 12, color: "#0A0A0A" }}>
            {clientId ? `Editando reglas de ${clientName || "este objetivo"}` : "Editando reglas globales (todos los objetivos)"}
          </b>
        </div>

        {clientId && rules && !ownRules && (
          <p style={{ fontSize: 11, color: "var(--invict-text-muted)", margin: "0 0 10px" }} data-testid="rules-uses-global">
            Este objetivo usa las reglas globales. Si cambiás un valor y guardás, quedan como reglas propias solo de este objetivo (las globales no se tocan).
          </p>
        )}
        {ownRules && (
          <p style={{ fontSize: 11, color: "#0f7a3d", fontWeight: 700, margin: "0 0 10px" }} data-testid="rules-own">
            Este objetivo tiene reglas propias: no se ve afectado por cambios en las globales.
          </p>
        )}
        {!clientId && (
          <p style={{ fontSize: 11, color: "var(--invict-text-muted)", margin: "0 0 10px" }}>
            Para adaptarlas a un objetivo puntual, elegilo en el filtro "Objetivo" del cronograma y volvé a abrir esta ventana.
          </p>
        )}
        <p style={{ fontSize: 11, color: "var(--invict-text-muted)", margin: "0 0 14px" }}>
          Cada regla puede solo AVISAR (deja guardar igual) o BLOQUEAR (no permite guardar) al romperse.
        </p>

        {!rules && <div className="skel" style={{ height: 120 }} />}
        {rules && RULE_FIELDS.map((f) => (
          <div key={f.key} style={{ display: "grid", gridTemplateColumns: "1fr 110px", gap: 8, alignItems: "end", marginBottom: 10 }}>
            <div className="field" style={{ margin: 0 }}>
              <label>{f.hoursLabel}</label>
              <input type="number" min="0" step="0.5" disabled={!canEdit} value={rules[f.numKey] ?? ""}
                onChange={(e) => setRules({ ...rules, [f.numKey]: Number(e.target.value) })}
                data-testid={`rules-${f.key}-value`} />
            </div>
            <div className="field" style={{ margin: 0 }}>
              <label>Modo</label>
              <select disabled={!canEdit} value={rules[f.modeKey]} onChange={(e) => setRules({ ...rules, [f.modeKey]: e.target.value })}
                data-testid={`rules-${f.key}-mode`}>
                <option value="alert">Alerta</option>
                <option value="block">Bloquea</option>
              </select>
            </div>
          </div>
        ))}
        {/* Fix AS (2026-09-25): fechas festivas (pedido del cliente para fin de año). */}
        {rules && (
          <div style={{ borderTop: "1px solid var(--invict-bone-3)", paddingTop: 10, marginTop: 4, marginBottom: 10 }} data-testid="rules-festive">
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 800, color: "#0A0A0A", cursor: canEdit ? "pointer" : "default" }}>
              <input type="checkbox" disabled={!canEdit} checked={!!rules.festive_enabled} onChange={(e) => setRules({ ...rules, festive_enabled: e.target.checked })} data-testid="rules-festive-enabled" />
              Fechas festivas: que no trabaje las dos fiestas
            </label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 110px", gap: 8, marginTop: 6 }}>
              <div className="field" style={{ margin: 0 }}><label>Si trabaja (DD/MM)</label>
                <input disabled={!canEdit} value={(rules.festive_a || []).join(", ")} onChange={(e) => setRules({ ...rules, festive_a: e.target.value.split(",").map((x) => x.trim()) })} data-testid="rules-festive-a" /></div>
              <div className="field" style={{ margin: 0 }}><label>Alerta si también (DD/MM)</label>
                <input disabled={!canEdit} value={(rules.festive_b || []).join(", ")} onChange={(e) => setRules({ ...rules, festive_b: e.target.value.split(",").map((x) => x.trim()) })} data-testid="rules-festive-b" /></div>
              <div className="field" style={{ margin: 0 }}><label>Modo</label>
                <select disabled={!canEdit} value={rules.festive_mode || "alert"} onChange={(e) => setRules({ ...rules, festive_mode: e.target.value })} data-testid="rules-festive-mode">
                  <option value="alert">Alerta</option>
                  <option value="block">Bloquea</option>
                </select></div>
            </div>
          </div>
        )}
        {canEdit && rules && (
          <button onClick={save} disabled={busy} className="btn-primary" style={{ width: "100%", padding: 10, marginTop: 6 }} data-testid="rules-save">
            {busy ? "Guardando..." : clientId ? (ownRules ? "Guardar reglas del objetivo" : "Definir reglas propias para este objetivo") : "Guardar reglas globales"}
          </button>
        )}
        {canEdit && ownRules && (
          <button onClick={backToGlobal} disabled={busy} className="btn-ghost" style={{ width: "100%", padding: 9, marginTop: 8, fontSize: 12 }} data-testid="rules-back-global">
            Volver a las reglas globales
          </button>
        )}

        {/* Prompt AF - Cambio 3: incompatibilidad entre 2 personas (GLOBAL, no depende del objetivo filtrado) */}
        <div style={{ marginTop: 22, paddingTop: 16, borderTop: "1px solid var(--invict-bone-3)" }} data-testid="incompatibility-section">
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
            <Users size={14} style={{ color: "#BF3437" }} />
            <h4 style={{ margin: 0, fontSize: 13, fontWeight: 900, color: "#0A0A0A" }}>Incompatibilidades entre personas</h4>
          </div>
          <p style={{ fontSize: 11, color: "var(--invict-text-muted)", margin: "0 0 12px" }}>
            Aplica en CUALQUIER objetivo/sede — si el par tiene turnos superpuestos, se avisa o se bloquea según el modo elegido. Nace en Alerta.
            Elegí si aplica al turno día, al turno noche o a ambos (noche = cruza la medianoche o empieza de 18:00 a 05:59).
          </p>

          {pairs === null && <div className="skel" style={{ height: 60 }} />}
          {pairs && pairs.length === 0 && (
            <div style={{ fontSize: 12, color: "var(--invict-text-muted)", marginBottom: 10 }} data-testid="incompatibility-empty">Sin reglas de incompatibilidad cargadas.</div>
          )}
          {pairs && pairs.map((p) => (
            <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: "1px solid var(--invict-bone-3)" }} data-testid={`incompatibility-row-${p.id}`}>
              <div style={{ flex: 1, fontSize: 12, fontWeight: 700, color: "#0A0A0A" }}>{p.user_a_name} ↔ {p.user_b_name}</div>
              <select disabled={!canEdit} value={p.applies_to || "ambos"} onChange={(e) => updatePairApplies(p.id, e.target.value)} data-testid={`incompatibility-applies-${p.id}`} style={{ fontSize: 11, padding: "4px 6px" }}>
                {Object.entries(APPLIES_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <select disabled={!canEdit} value={p.mode} onChange={(e) => updatePairMode(p.id, e.target.value)} data-testid={`incompatibility-mode-${p.id}`} style={{ fontSize: 11, padding: "4px 6px" }}>
                <option value="alert">Alerta</option>
                <option value="block">Bloquea</option>
              </select>
              {canEdit && (
                <button onClick={() => removePair(p.id)} style={{ border: "none", background: "transparent", cursor: "pointer", color: "#BF3437" }} data-testid={`incompatibility-remove-${p.id}`}>
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}

          {canEdit && (
            <div style={{ marginTop: 12 }} data-testid="incompatibility-add-form">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                <div className="field" style={{ margin: 0 }}>
                  <label>Persona A</label>
                  <select value={newPair.user_a_id} onChange={(e) => setNewPair({ ...newPair, user_a_id: e.target.value })} data-testid="incompatibility-user-a">
                    <option value="">Elegir...</option>
                    {roster.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div className="field" style={{ margin: 0 }}>
                  <label>Persona B</label>
                  <select value={newPair.user_b_id} onChange={(e) => setNewPair({ ...newPair, user_b_id: e.target.value })} data-testid="incompatibility-user-b">
                    <option value="">Elegir...</option>
                    {roster.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="field" style={{ margin: "0 0 8px" }}>
                <label>¿Para qué turno es la incompatibilidad?</label>
                <select value={newPair.applies_to} onChange={(e) => setNewPair({ ...newPair, applies_to: e.target.value })} data-testid="incompatibility-new-applies">
                  {Object.entries(APPLIES_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 110px", gap: 8, alignItems: "end" }}>
                <div className="field" style={{ margin: 0 }}>
                  <label>Modo</label>
                  <select value={newPair.mode} onChange={(e) => setNewPair({ ...newPair, mode: e.target.value })} data-testid="incompatibility-new-mode">
                    <option value="alert">Alerta</option>
                    <option value="block">Bloquea</option>
                  </select>
                </div>
                <button type="button" onClick={addPair} disabled={pairBusy} className="btn-primary" style={{ padding: 10 }} data-testid="incompatibility-add-btn">
                  <Plus size={13} style={{ marginRight: 4 }} />{pairBusy ? "..." : "Agregar"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
